<template>
  <div class="plant-image-area">
    <div v-if="slides.length" class="plant-carousel">
    <b-carousel
      id="plant-carousel"
      v-model="slide"
      :interval="5000"
      controls
      indicators
      label-next=""
      label-prev=""
      background="#fff3e0"
      img-width="960"
      img-height="480"
    >
      <b-carousel-slide
        v-for="(item, index) in slides"
        :key="index"
        :img-src="item.url"
        :img-alt="item.alt"
      >
        <p v-if="item.author" class="plant-carousel__author">{{ $t('author') }}: {{ item.author }}</p>
      </b-carousel-slide>
    </b-carousel>
    </div>
    <div v-else-if="searchQuery" class="plant-image-fallback">
    <p class="plant-image-fallback__label">{{ $t('no_photos') }}</p>
    <div v-if="suggestedPhotos.length" class="plant-image-fallback__grid">
      <a
        v-for="(photo, index) in suggestedPhotos"
        :key="index"
        class="plant-image-fallback__thumb"
        :href="photo.source_url"
        target="_blank"
        rel="noopener noreferrer"
      >
        <img :src="photo.url" :alt="alt || searchQuery" loading="lazy">
      </a>
    </div>
    <a
      class="plant-image-fallback__link"
      :href="googleImagesUrl"
      target="_blank"
      rel="noopener noreferrer"
    >
      {{ $t('search_google_images') }}
    </a>
    </div>
  </div>
</template>

<script>
import { googleImagesSearchUrl, resolvePlantsApiRoot, resolveMediaUrl } from '@/utils'

export default {
  props: {
    images: {
      type: Array,
      default: () => []
    },
    alt: {
      type: String,
      default: ''
    },
    searchQuery: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      slide: 0,
      suggestedPhotos: []
    }
  },
  computed: {
    slides () {
      return (this.images || [])
        .map((image) => {
          const url = resolveMediaUrl(image.image || image.original_url)
          if (!url) {
            return null
          }
          return {
            url,
            author: image.author || '',
            alt: this.alt
          }
        })
        .filter(Boolean)
    },
    googleImagesUrl () {
      return googleImagesSearchUrl(this.searchQuery)
    }
  },
  watch: {
    searchQuery: {
      immediate: true,
      handler () {
        this.loadSuggestedPhotos()
      }
    },
    slides () {
      this.loadSuggestedPhotos()
    }
  },
  methods: {
    async loadSuggestedPhotos () {
      if (this.slides.length || !(this.searchQuery || '').trim()) {
        this.suggestedPhotos = []
        return
      }
      try {
        const params = new URLSearchParams({ q: this.searchQuery.trim() })
        const response = await fetch(`${resolvePlantsApiRoot()}api/image_search?${params}`)
        if (!response.ok) {
          this.suggestedPhotos = []
          return
        }
        const data = await response.json()
        this.suggestedPhotos = data.photos || []
      } catch (error) {
        this.suggestedPhotos = []
      }
    }
  }
}
</script>

<style>
.plant-carousel {
  width: 100%;
  margin: 0.5rem 0 0.75rem;
}

.plant-carousel .carousel-item img {
  width: 100%;
  max-height: 320px;
  object-fit: cover;
}

.plant-carousel__author {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: #1c1c1c;
}

.plant-carousel .carousel-control-prev-icon,
.plant-carousel .carousel-control-next-icon {
  width: 2rem;
  height: 2rem;
}

.plant-carousel .carousel-indicators {
  position: absolute;
  right: 0;
  bottom: 0.65rem;
  left: 0;
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0;
  padding: 0;
  list-style: none;
}

.plant-carousel .carousel-indicators li,
.plant-carousel .carousel-indicators button {
  box-sizing: content-box;
  flex: 0 0 auto;
  width: 0.55rem;
  height: 0.55rem;
  margin: 0 0.22rem;
  padding: 0;
  text-indent: -9999px;
  overflow: hidden;
  cursor: pointer;
  color: transparent;
  font-size: 0;
  line-height: 0;
  background-color: rgba(232, 220, 200, 0.95);
  background-clip: padding-box;
  border: 0;
  border-top: 0;
  border-bottom: 0;
  border-radius: 50%;
  opacity: 1;
  transition: background-color 0.15s ease, transform 0.15s ease;
}

.plant-carousel .carousel-indicators li::before,
.plant-carousel .carousel-indicators li::after,
.plant-carousel .carousel-indicators button::before,
.plant-carousel .carousel-indicators button::after {
  content: none;
}

.plant-carousel .carousel-indicators li.active,
.plant-carousel .carousel-indicators button.active,
.plant-carousel .carousel-indicators .active {
  width: 0.55rem;
  height: 0.55rem;
  background-color: #ff9900;
  transform: scale(1.15);
}

.plant-image-fallback {
  margin: 0.5rem 0 0.75rem;
  padding: 0.75rem;
  border: 1px dashed #e8dcc8;
  border-radius: 6px;
  background: #fff8ec;
  text-align: center;
}

.plant-image-fallback__label {
  margin: 0 0 0.5rem;
  font-size: 0.8125rem;
  color: #666;
}

.plant-image-fallback__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.35rem;
  margin-bottom: 0.65rem;
}

.plant-image-fallback__thumb {
  display: block;
  overflow: hidden;
  border-radius: 4px;
  border: 1px solid #e8dcc8;
}

.plant-image-fallback__thumb img {
  display: block;
  width: 100%;
  height: 72px;
  object-fit: cover;
}

.plant-image-fallback__link {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  background: #ff9900;
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 600;
  text-decoration: none;
}

.plant-image-fallback__link:hover,
.plant-image-fallback__link:focus {
  background: #e07c00;
  color: #fff;
  text-decoration: none;
}

@media (max-width: 575px) {
  .plant-image-fallback__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
